import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { UserHasAccess, UserHasAccessActions } from 'components/user-access';
import { Filter, FilterButton } from 'components/filtering';
import { FormTextInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { StatusBadge } from 'components/status';
import { Numeric } from 'components/formatting';
import { DateToLocal } from 'components/dates';
import { ShowHide } from 'components/layout';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { useDebounce } from 'hooks/useDebounce';
import { BulkImportService } from 'services';

import { importSources, importTypes } from './bulkImportTypes';
import { buildComparator } from './listingComparator';
import { buildFailureWarningText, splitSettledSources } from './sourceFanOut';
import { BulkImports } from './BulkImports';

const defaultArray = [];

const debounceTimeout = 1500;
const badRequestCode = 400;
const fetchRequestCount = 500;
const defaultPageSize = 25;
const generalErrorMessage = "An error occurred while loading the import history. Please try again.";

// The legacy import history is hosted by the platform's main app, so a user holding any of
// these import claims should be offered the link to it.
const legacyImportActions = [
    'Employees:Import',
    'Training:Import',
    'Collisions:Import',
    'PlannedMaintenance:Import',
    'PrePlan:Import',
    'DCVU:Import'
];

const statusFilterName = "STATUS";
const typeFilterName = "TYPE";

const statusFilterItems = [
    { id: "PARTIAL", name: "Awaiting mapping" },
    { id: "Queued", name: "Queued" },
    { id: "Processing", name: "Processing" },
    { id: "Completed", name: "Completed" },
    { id: "CompletedWithErrors", name: "Completed With Errors" },
    { id: "Failed", name: "Failed" }
];

// A partial row is an un-mapped upload draft — it has no wire status, so its pill is keyed
// off isPartial rather than the status string.
const awaitingMappingStatusDisplay = { label: "Awaiting mapping", iconName: "one-to-many", intent: Intent.WARNING, animated: false };

// A partial row's file name routes back into the New Import wizard at the mapping step (the
// wizard resumes from the uploadId/importType query params) rather than to a detail page.
// The wizard resolves the type by its key, which the registry keys by typeName.
function getResumeMappingLink(row) {
    const importType = importTypes.find((type) => type.typeName === row.bulkImportTypeName) ?? null;
    const importTypeKey = importType == null ? "" : importType.bulkImportTypeKey;
    return `/import/bulk/new?uploadId=${row.bulkImportUploadId}&importType=${importTypeKey}`;
}

// The microservice source that hosts an import type, resolved from the registry by the type's
// wire key. Used to narrow the history fan-out to a single source once a type is selected.
function getTypeSourceKey(bulkImportTypeKey) {
    return importTypes.find((type) => type.bulkImportTypeKey === bulkImportTypeKey)?.microserviceKey ?? null;
}

// Maps a wire status string to the pill's label, Blueprint icon, and intent. Unknown
// statuses fall back to a neutral pill so an unexpected value never breaks the row.
function getStatusDisplay(status) {
    switch (status) {
        case "Queued":
            return { label: "Queued", iconName: "time", intent: Intent.NONE, animated: false };
        case "Processing":
            return { label: "Processing", iconName: "refresh", intent: Intent.PRIMARY, animated: true };
        case "Completed":
            return { label: "Completed", iconName: "tick-circle", intent: Intent.SUCCESS, animated: false };
        case "Completed With Errors":
            return { label: "Completed With Errors", iconName: "warning-sign", intent: Intent.WARNING, animated: false };
        case "Failed":
            return { label: "Failed", iconName: "error", intent: Intent.DANGER, animated: false };
        default:
            return { label: status ?? "Unknown", iconName: "help", intent: Intent.NONE, animated: false };
    }
}

const tableHeaders = [
    "File name",
    "Type",
    "Total Rows",
    "Imported",
    "Errors",
    "Created",
    "Completed",
    "Status"
];

// The sortName identifies which row field the in-memory comparator orders by, and how to
// coerce its values for comparison (string, number, or date).
const sortableHeaders = [
    { headerName: "File name", sortName: "fileName" },
    { headerName: "Type", sortName: "bulkImportTypeName" },
    { headerName: "Total Rows", sortName: "totalRows" },
    { headerName: "Imported", sortName: "successRows" },
    { headerName: "Errors", sortName: "errorRows" },
    { headerName: "Created", sortName: "createdDate" },
    { headerName: "Completed", sortName: "completedDate" },
    { headerName: "Status", sortName: "status" }
];

const defaultSortHeader = sortableHeaders.find((header) => header.sortName === "createdDate");

const numericSortFields = ["totalRows", "successRows", "errorRows"];
const dateSortFields = ["createdDate", "completedDate"];

// The Import History tab's listing body — the fan-out fetch, search, filters, and the
// in-memory sorted/paged table. The page shell (heading, New Import button, tabs) lives in
// BulkImports.
function BulkImportHistoryPanel() {

    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(defaultArray);

    // The full fetched set is held in state and sorted/sliced in memory; only the search
    // term and filters trigger a re-fetch.
    const [allImports, setAllImports] = useState(defaultArray);

    // Set when some sources returned rows and others failed, so the rendered list is partial.
    const [sourceFailureText, setSourceFailureText] = useState(null);

    const [sortedBy, setSortedBy] = useState(defaultSortHeader);
    const [sortedDir, setSortedDir] = useState("D");

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const inputSearchRef = useRef(null);

    const userActions = useSelector((state) => state.loggedInUser.actions);

    // The type options are the non-legacy types the user holds the claim for — legacy types have
    // no history source here, and a type the user cannot import is one they can have no rows for.
    // Unlike the status items these cannot be module-level, because the claim check needs the
    // logged-in user's actions.
    const typeFilterItems = importTypes
        .filter((type) => !type.isLegacy && UserHasAccessActions([type.requiredClaim], userActions))
        .map((type) => ({ id: type.bulkImportTypeKey, name: type.typeName }));

    const filterName = "bulkImports";
    const pageFilters = [
        {
            name: statusFilterName,
            displayName: "Import Status",
            items: statusFilterItems,
            singleSelect: true
        },
        {
            name: typeFilterName,
            displayName: "Import Type",
            items: typeFilterItems,
            singleSelect: true
        }
    ];

    // Only fetch the history sources the user's claims allow; each source carries the claims of
    // the types it hosts, and holding any one grants access. A user with no accessible source can
    // still reach the page (to start a new import) but sees no history table.
    const accessibleSources = importSources.filter((source) => UserHasAccessActions(source.requiredClaims, userActions));
    const canViewHistory = accessibleSources.length > 0;

    const showLegacyLink = UserHasAccess(legacyImportActions);

    // The selected status (if any) is the value of the STATUS filter selection.
    const selectedStatus = appliedFilters.find((f) => f.name === statusFilterName)?.value ?? null;

    // No TYPE selection means all types, which is the default and leaves the fan-out unnarrowed.
    const selectedType = appliedFilters.find((f) => f.name === typeFilterName)?.value ?? null;

    function fetchImports() {
        setIsLoading(true);

        const filters = [];
        if (selectedStatus != null) {
            filters.push({ key: statusFilterName, value: selectedStatus });
        }
        if (selectedType != null) {
            filters.push({ key: typeFilterName, value: selectedType });
        }

        // With all types selected every accessible source is queried. Selecting one type narrows
        // the fan-out to the single microservice that hosts it, since no other source holds rows
        // of that type. The narrowed entry is taken from the accessible sources rather than built
        // here, so it keeps the requiredClaims the failure reporting reads.
        let queriedSources = accessibleSources;
        if (selectedType != null) {
            queriedSources = accessibleSources.filter((source) => source.sourceKey === getTypeSourceKey(selectedType));
        }

        const searchRequest = {
            requestCount: fetchRequestCount,
            pageNumber: 1,
            sortField: "createdDate",
            sortDirection: "D",
            searchTerm: debouncedSearchTerm,
            filters: filters
        };

        // One request per queried source; each row keeps its source so the file-name link can
        // route to the correct microservice. A source can host multiple import types, so its
        // single request returns every type's rows for that microservice.
        const requests = queriedSources.map((source) => {
            return BulkImportService.searchImports(source.sourceKey, searchRequest).then(
                (response) => {
                    return (response?.data ?? []).map((row) => {
                        return { ...row, sourceKey: source.sourceKey };
                    });
                }
            );
        });

        // Settled rather than all-or-nothing: one unavailable microservice must not blank the
        // whole listing when the others answered.
        Promise.allSettled(requests).then((settledResults) => {
            const { rows, failures } = splitSettledSources(queriedSources, settledResults);

            // The failures.length > 0 guard matters because the filter selection is persisted:
            // a stored type whose claim the user has since lost narrows to no source at all, and
            // an empty fan-out must render an empty list rather than report a failure.
            if (failures.length > 0 && failures.length === queriedSources.length) {
                // Nothing loaded, so there is no partial list to caption — the failure is
                // reported as a toast, driven off the first rejection.
                const firstFailure = failures[0].reason;
                if (firstFailure?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, firstFailure.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
                setSourceFailureText(null);
            } else if (failures.length > 0) {
                // A partial list is a quieter failure than an empty one, so the warning has to
                // persist alongside the rows rather than disappear like a toast.
                setSourceFailureText(buildFailureWarningText(failures, "imports", userActions));
            } else {
                setSourceFailureText(null);
            }

            setAllImports(rows);
            setPageNumber(1);
            setIsLoading(false);
        });
    }

    useEffect(() => {
        // Only fetch when the user can access at least one history source; searchImports would
        // 403 otherwise, so skip the fetch and clear the loading state for them.
        if (canViewHistory) {
            fetchImports();
        } else {
            setIsLoading(false);
        }
    }, [debouncedSearchTerm, selectedStatus, selectedType]);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onToggleFilter() {
        setShowFilters((previousState) => {
            return !previousState;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    // Sorting and paging are client-side only — they re-order or re-slice the in-memory
    // array without re-fetching.
    function onTableSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    const comparator = buildComparator({
        sortField: sortedBy.sortName,
        direction: sortedDir,
        numericSortFields: numericSortFields,
        dateSortFields: dateSortFields,
        tieBreakField: "fileName"
    });
    const sortedImports = [...allImports].sort(comparator);

    const pageStart = (pageNumber - 1) * pageSize;
    const pageRows = sortedImports.slice(pageStart, pageStart + pageSize);

    const tableRows = pageRows.map((row) => {
        // Partial rows have no bulkImportId, so their cells key off the upload id instead.
        let rowIdentifier = row.bulkImportId;
        if (row.bulkImportId == null) {
            rowIdentifier = `upload-${row.bulkImportUploadId}`;
        }
        const rowKey = `${row.sourceKey}-${rowIdentifier}`;

        let statusDisplay;
        let fileLinkTarget;
        let totalRowsValue = row.totalRows;
        let successRowsValue = row.successRows;
        let errorRowsValue = row.errorRows;
        let completedDateValue = row.completedDate;

        if (row.isPartial) {
            statusDisplay = awaitingMappingStatusDisplay;
            fileLinkTarget = getResumeMappingLink(row);
            // A draft has no processed counts, so every metric cell renders as absent even
            // though the API supplies totalRows for partial rows.
            totalRowsValue = null;
            successRowsValue = null;
            errorRowsValue = null;
            completedDateValue = null;
        } else {
            statusDisplay = getStatusDisplay(row.status);
            fileLinkTarget = `/import/bulk/${row.sourceKey}/${row.bulkImportId}`;
        }

        return [
            <Link key={`file-${rowKey}`} to={fileLinkTarget}>{row.fileName}</Link>,
            row.bulkImportTypeName,
            <Numeric key={`total-${rowKey}`}>{totalRowsValue}</Numeric>,
            <Numeric key={`success-${rowKey}`}>{successRowsValue}</Numeric>,
            <Numeric key={`errors-${rowKey}`}>{errorRowsValue}</Numeric>,
            <DateToLocal key={`created-${rowKey}`} format="DD MMM YYYY">{row.createdDate ?? "-"}</DateToLocal>,
            <DateToLocal key={`completed-${rowKey}`} format="DD MMM YYYY">{completedDateValue ?? "-"}</DateToLocal>,
            <StatusBadge
                key={`status-${rowKey}`}
                text={statusDisplay.label}
                iconName={statusDisplay.iconName}
                intent={statusDisplay.intent}
                animated={statusDisplay.animated}
            />
        ];
    });

    return (
        <>
            <ShowHide
                evaluator={canViewHistory}
                show={(
                    <>
                        <div className="filter-controls">
                            <div className="inline-items spacer-bottom-small">
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput
                                            inputRef={inputSearchRef}
                                            placeholder="Search file name"
                                            onChange={onSearchChange}
                                            value={searchTerm}
                                            large
                                            disabled={isLoading && inputSearchRef.current !== document?.activeElement}
                                            icon="search"
                                            id="bulk-import-search-field"
                                        />
                                    </div>
                                </div>
                                <div className="inline-item">
                                    <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                </div>
                            </div>
                            <Filter
                                filterName={filterName}
                                visible={showFilters}
                                filters={pageFilters}
                                onUpdate={onFilterChange}
                                onClickOutside={onToggleFilter}
                            />
                        </div>

                        <NotificationInline
                            intent="warning"
                            show={sourceFailureText != null}
                            allowClose={false}
                            text={sourceFailureText}
                        />

                        <ListingTable
                            id="bulk-import-history-table"
                            showPerPage={true}
                            pageSize={defaultPageSize}
                            headers={tableHeaders}
                            data={tableRows}
                            totalRecordCount={sortedImports.length}
                            loadingData={isLoading}
                            onPagingChange={onPagingChange}
                            // Sorting re-slices in memory without changing the row count, which is the
                            // only thing the table re-seats its own highlighted page on. Feeding the
                            // page from here keeps that highlight on the slice actually rendered.
                            startPage={pageNumber}
                            sortable
                            sortableHeaders={sortableHeaders}
                            sortedBy={sortedBy}
                            sortedDir={sortedDir}
                            onSort={onTableSort}
                            noDataMessage="There are no imports to display."
                        />
                    </>
                )}
            />

            <ShowHide
                evaluator={showLegacyLink}
                show={(
                    <p className="spacer-top">
                        Can't see your import? It may be in the <a href={`${window.env.VISION_URL}/#/ImportList`}>legacy import history</a>.
                    </p>
                )}
            />
        </>
    );
}

export function BulkImportHistory({ breadcrumbs = defaultArray }) {
    return (
        <BulkImports breadcrumbs={breadcrumbs} selectedTabIndex={0}>
            <BulkImportHistoryPanel />
        </BulkImports>
    );
}

BulkImportHistory.propTypes = {
    breadcrumbs: PropTypes.array
};
